"use client";

import React, { useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Tooltip,
  Progress,
  Switch,
  Image,
  PopoverTrigger,
  Popover,
  PopoverContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { useTranslation } from "react-i18next";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { FileUploadError } from "@/components/FileUpload/FileUploadError";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useTheme } from "@/hooks/use-theme";
import { useFileUploadError } from "@/hooks/useFileUploadError";
import { isSpecializedWorkflow, getWorkflowDefaultPrompt } from "@/utils/workflowUtils";
import { useChatStore, UploadedFile } from "@/stores/ChatStore";
import { convertFileTypeToIcon } from "@/utils/ConvertFileTypeToIcon";
import { ConfirmationDialog } from "@/components/UI/ConfirmationDialog";
import { useConfirmationDialog } from "@/hooks/useConfirmationDialog";
import { blurActiveElement } from "@/utils/dom";
import {
  toggleWorkflowTool as toggleTool,
  createDefaultChatWorkflow,
} from "@/utils/chat/workflowTools";
import { createStructuredUploadError } from "@/utils/chat/validationHelpers";
// ✅ NEW: Import our refactored hooks
import { useFileUpload } from "@/hooks/chat/useFileUpload";
import { usePasteHandler } from "@/hooks/chat/usePasteHandler";

import { PromptInputInvestAI } from "./PromptInputInvestAI";
import { ChatSettingsModal } from "./ChatSettingsModal";
import { ChatModelSelector } from "./ChatModelSelector";
import { PromptSearchModal } from "./PromptSearchModal";
import { FeatureCardProps } from "./FeatureCards";
import { FeatureCards } from "./FeatureCards";
import { PromptSuggestions, PromptSuggestion } from "./PromptSuggestions";

interface PromptWithActionsProps {
  submit: (prompt: string, files: UploadedFile[]) => Promise<void>;
  streaming: boolean;
  suggestions: PromptSuggestion[];
  features: FeatureCardProps[];
  showFileUpload?: boolean;
  showVoice?: boolean;
  showPrompts?: boolean;
  showPrompto?: boolean;
  showSearch?: boolean;
  showKnowledgeHub?: boolean;
  showSettings?: boolean;
  showChatModelSelector?: boolean;
  messagesCount?: number;
}

const ALLOWED_FILE_TYPES = [
  ".pdf",
  ".doc",
  ".docx",
  ".pptx",
  ".png",
  ".jpg",
  ".jpeg",
  ".svg",
  ".csv",
  ".xlsx",
  ".xls",
  ".txt",
  ".md",
  ".json",
  ".yaml",
  ".yml",
  ".xml",
];

const TooltipPopover = React.memo(function TooltipPopover({
  children,
  padding = "p-2",
  width = "w-[280px]",
}: {
  children: React.ReactNode;
  padding?: string;
  width?: string;
}) {
  return (
    <Popover backdrop="transparent" placement="top">
      <PopoverTrigger>
        <Icon
          className="text-brand-primary dark:!text-white"
          icon="solar:info-circle-linear"
          width={10}
        />
      </PopoverTrigger>
      <PopoverContent className={`bg-white ${padding} rounded-xl shadow-xl ${width}`}>
        {children}
      </PopoverContent>
    </Popover>
  );
});

export const PromptWithActionsInvestAI = ({
  submit,
  streaming,
  suggestions,
  features,
  showFileUpload,
  showVoice,
  showPrompts,
  showPrompto,
  showSearch,
  showKnowledgeHub,
  showSettings,
  showChatModelSelector,
  messagesCount = 0,
}: PromptWithActionsProps) => {
  const [prompt, setPrompt] = React.useState<string>("");
  const [isOCRMode, setIsOCRMode] = React.useState(false);
  const [initialPrompt, setInitialPrompt] = React.useState<string>("");

  const { platformClient, adminClient } = useWebuildAPIClient();
  const { getWorkflow, setWorkflow } = useWorkflowStore();
  const { runId, stopStreaming, resetPrompt, clearResetPrompt, chat } = useChatStore();
  const { brand } = useBrand();
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const workflow = useWorkflowStore((s) => s.workflow);
  const { t, i18n } = useTranslation();

  // Determine Configuration button disabled state (same logic as ChatModelSelector)
  const hasMessages = chat?.messages && chat?.messages.length > 0;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isConfigurationDisabled = isSpecialized || hasMessages;

  // Check if workflow has a pre-filled default prompt value
  const workflowDefaultPrompt = getWorkflowDefaultPrompt(workflow);
  const isPromptLocked = !!workflowDefaultPrompt;

  // File upload error handling with retry functionality
  const { errorState, clearError, retryUpload, setUploadError } = useFileUploadError(
    async (file: File) => {
      // Retry upload by simulating file input
      const dataTransfer = new DataTransfer();

      dataTransfer.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        const event = new window.Event("change", { bubbles: true });

        fileInputRef.current.dispatchEvent(event);
      }
    },
  );

  // ✅ NEW: Use our custom file upload hook
  const {
    files: uploadedFiles,
    uploadProgress,
    isUploading,
    failedFiles,
    fileInputRef,
    error: uploadError,
    handleFileChange,
    handleDrop: handleFileDrop,
    removeFile,
    retryFailedUploads,
    clearFiles,
  } = useFileUpload({
    onUpload: useCallback(
      async (files: File[]) => {
        let chatId = runId;

        if (!chatId && platformClient && user) {
          const { create } = useChatStore.getState();
          const userId = user.id;
          const userTenant = user.tenant || "default";

          // Get the current workflow or use a default chat workflow
          const currentWorkflow = workflow || createDefaultChatWorkflow("gpt-4.1");

          chatId = await create(platformClient, currentWorkflow, userId, userTenant, "File Upload");
        }

        if (!chatId) {
          throw new Error("Failed to create or retrieve chat ID");
        }

        const uploadedFiles: UploadedFile[] = await Promise.all(
          files.map(async (file) => {
            const response = await platformClient?.files.upload({
              bodyUploadFileBackgroundPlatformV1DataFilesPost: {
                file: file,
                summary: file.name,
                tags: [],
                fileType: file.type,
                chatId: chatId,
                private: true,
                parser: isOCRMode ? "ocr" : "llm",
              },
            });

            if (!response) {
              throw new Error(`Upload failed for ${file.name}`);
            }

            // Poll job status until processing completes
            let jobStatus = await platformClient?.files.getJobStatus({
              jobId: response.job_id,
            });

            while (jobStatus?.status === "pending") {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              jobStatus = await platformClient?.files.getJobStatus({
                jobId: response.job_id,
              });
            }

            // Handle failed uploads (e.g., quarantined files)
            if (jobStatus?.status === "failed") {
              const errorMessage =
                jobStatus.result ||
                t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" });

              // Attach the full job status as structured error data for proper error parsing
              const error = createStructuredUploadError(errorMessage, jobStatus);

              throw error;
            }

            return {
              name: file.name,
              size: String(file.size),
              type: file.type,
              id: jobStatus?.result,
              status: t("Completed"),
            };
          }),
        );

        return uploadedFiles;
      },
      [platformClient, user, runId, workflow, isOCRMode, t],
    ),

    onValidate: (_files: File[]) => {
      // Simple synchronous validation - async validation will be handled by onUpload
      return { isValid: true, errors: [] };
    },
    maxFiles: 10,
  });

  // Sync upload error with error state
  React.useEffect(() => {
    if (uploadError && failedFiles.length > 0) {
      setUploadError(uploadError, failedFiles[0], user?.id, user?.tenant);
    } else if (!uploadError && failedFiles.length === 0) {
      // Clear error when upload succeeds or is cleared
      clearError();
    }
  }, [uploadError, failedFiles, setUploadError, clearError, user]);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = React.useState(false);
  const [isKnowledgeHubEnabled, setIsKnowledgeHubEnabled] = React.useState(false);
  // voice
  const { transcript, listening, browserSupportsSpeechRecognition, resetTranscript } =
    useSpeechRecognition();

  const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [speechError, setSpeechError] = React.useState<string | null>(null);

  const listeningRef = useRef(false);

  // Check if we're currently on the Prompto workflow page
  const isOnPromptoWorkflow = location.pathname === "/workflows/prompt_generator_workflow";

  // Confirmation dialog hook
  const {
    isOpen: isConfirmDialogOpen,
    showConfirmation,
    confirmationProps,
  } = useConfirmationDialog();

  // Check for secure context
  React.useEffect(() => {
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setSpeechError("Speech recognition requires HTTPS in production");
    }
  }, []);

  // Clear prompt and uploaded files when resetPrompt is triggered
  React.useEffect(() => {
    if (resetPrompt) {
      setPrompt("");
      clearFiles();
      clearResetPrompt();
    }
  }, [resetPrompt, clearResetPrompt, clearFiles]);

  // Pre-fill prompt from workflow default value when workflow changes
  React.useEffect(() => {
    const defaultPrompt = getWorkflowDefaultPrompt(workflow);

    if (defaultPrompt) {
      setPrompt(defaultPrompt);
    }
  }, [workflow, setPrompt]);

  const handleSubmit = async (prompt: string) => {
    if (listeningRef.current) {
      try {
        SpeechRecognition.stopListening();
        listeningRef.current = false;
        setSpeechError(null);
      } catch {
        setSpeechError("Failed to stop speech recognition");
      }
    }

    const fileToUpload = uploadedFiles;

    setPrompt(""); // Clear the prompt after submission
    setInitialPrompt(""); // Clear voice input initial prompt to prevent stale state
    resetTranscript(); // Clear any lingering voice transcript
    clearFiles();
    await submit(prompt, fileToUpload);
  };

  const handleSubmitEvent = async (event: React.FormEvent | React.MouseEvent) => {
    event.preventDefault();
    if (!prompt) {
      return;
    }
    await handleSubmit(prompt);
  };

  // removed: button-based web search toggling in favor of a Switch control

  const toggleWorkflowTool = useCallback(
    (tool: string) => {
      const currentWorkflow = getWorkflow();
      const updatedWorkflow = toggleTool(currentWorkflow, tool);

      if (updatedWorkflow) {
        setWorkflow(updatedWorkflow);
      }
    },
    [getWorkflow, setWorkflow],
  );

  const setWorkflowTool = (toolOrTools: string | string[], isSelected: boolean) => {
    if (!workflow?.agents) return;

    const chatAgentEntry = Object.entries(workflow.agents).find(
      ([_, agent]) => agent.type === "chat_agent",
    );

    if (!chatAgentEntry) return;

    const [agentId, agent] = chatAgentEntry;
    const currentTools: string[] = agent?.parameters?.tools || [];
    const toolsToModify = Array.isArray(toolOrTools) ? toolOrTools : [toolOrTools];

    let updatedTools: string[];

    if (isSelected) {
      // Add tools if not already present
      updatedTools = [
        ...currentTools,
        ...toolsToModify.filter((tool) => !currentTools.includes(tool)),
      ];
    } else {
      // Remove tools if present
      updatedTools = currentTools.filter((tool) => !toolsToModify.includes(tool));
    }

    const updatedAgent = {
      ...agent,
      parameters: {
        ...agent.parameters,
        tools: updatedTools,
      },
    };

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agentId]: updatedAgent,
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const handleWebSearchToggle = async (val: boolean) => {
    // If there's an active chat, show confirmation dialog
    if (runId) {
      const action = val ? "enable" : "disable";
      const confirmed = await showConfirmation({
        title: t("websearch-change-setting-title"),
        message: t("websearch-confirmation-message", { action }),
        confirmText: t("Continue"),
        cancelText: t("Cancel"),
        confirmColor: "primary",
        icon: "solar:settings-bold",
      });

      if (!confirmed) {
        // User cancelled, don't change anything
        return;
      }

      // User confirmed, update tools and start new chat
      setWorkflowTool(["serpapi_search", "crawler"], val);
      setIsSearchEnabled(val);

      // Start new chat to apply changes since backend locks in tools on session creation
      const { newChat } = useChatStore.getState();

      newChat();
    } else {
      // No active chat, just update the state
      setIsSearchEnabled(val);
      setWorkflowTool(["serpapi_search", "crawler"], val);
    }

    // Store web search preference in localStorage
    localStorage.setItem("webSearchEnabled", JSON.stringify(val));
  };

  const handleKnowledgeHub = () => {
    toggleWorkflowTool("knowledge_base_retriever");
    setIsKnowledgeHubEnabled(!isKnowledgeHubEnabled);
    // Remove focus to prevent Enter key from toggling again
    setTimeout(() => {
      blurActiveElement();
    }, 0);
  };

  // File upload functions are now handled by useFileUpload hook

  // Failed files are now managed by useFileUpload hook

  const handleTemplateSelect = (template: string) => {
    setPrompt(template);
    setIsTemplateModalOpen(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleVoicePress = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) {
      setSpeechError("Your browser doesn't support speech recognition");

      return;
    }
    if (listeningRef.current) {
      // STOP
      try {
        SpeechRecognition.stopListening();
        listeningRef.current = false;
        setSpeechError(null);
      } catch {
        setSpeechError("Failed to stop speech recognition");
        try {
          SpeechRecognition.abortListening();
        } catch {
          // Silently ignore abort errors
        }
      }
    } else {
      setInitialPrompt(prompt);
      try {
        await SpeechRecognition.startListening({
          continuous: true,
          interimResults: true,
          language: i18n.language,
        });
        listeningRef.current = true;
        setSpeechError(null);
      } catch {
        setSpeechError("Failed to start speech recognition");
        if (window.location.protocol !== "https:") {
          setSpeechError("Speech recognition requires HTTPS in production");
        }
      }
    }
    setTimeout(() => {
      blurActiveElement();
    }, 0);
  }, [browserSupportsSpeechRecognition, prompt, i18n.language]);

  // Track if we've ever been listening (to avoid resetting on initial render)
  const hasListenedRef = useRef(false);

  // Sync listeningRef with actual listening state from the hook
  useEffect(() => {
    listeningRef.current = listening;
    if (listening) {
      hasListenedRef.current = true;
    }
  }, [listening]);

  // Update prompt with transcript when actively listening
  useEffect(() => {
    if (listening && transcript) {
      setPrompt(initialPrompt + (initialPrompt ? " " : "") + transcript);
    }
  }, [transcript, initialPrompt, listening]);

  // Reset transcript when listening stops (only after we've actually listened)
  useEffect(() => {
    if (!listening && hasListenedRef.current) {
      // delay a tick to let last transcript event settle, then clear
      const id = setTimeout(() => {
        resetTranscript();
        hasListenedRef.current = false;
      }, 100);

      return () => clearTimeout(id);
    }
  }, [listening, resetTranscript]);

  useEffect(() => {
    return () => {
      try {
        SpeechRecognition.stopListening();
        resetTranscript();
        listeningRef.current = false;
      } catch {
        // Silently ignore stop listening errors
      }
    };
  }, [resetTranscript]);

  // Initialize web search toggle from localStorage first, then workflow
  useEffect(() => {
    // Check localStorage for saved preference
    const savedPreference = localStorage.getItem("webSearchEnabled");

    if (savedPreference !== null) {
      const enabled = JSON.parse(savedPreference);

      setIsSearchEnabled(enabled);

      // Apply the saved preference to the workflow
      if (workflow) {
        setWorkflowTool(["serpapi_search", "crawler"], enabled);
      }

      return;
    }

    // Fallback to workflow state if no saved preference
    if (workflow?.agents) {
      const chatAgent = Object.values(workflow.agents).find((agent) => agent.type === "chat_agent");

      if (chatAgent) {
        const tools = chatAgent.parameters.tools || [];
        const enabled = tools.includes("serpapi_search") || tools.includes("crawler");

        setIsSearchEnabled(enabled);
      }
    }
  }, [workflow]);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      // Use the hook's handleFileChange method
      const fileList = new DataTransfer();

      Array.from(files).forEach((file) => fileList.items.add(file));

      if (fileInputRef.current) {
        fileInputRef.current.files = fileList.files;
        await handleFileChange({
          target: fileInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handlePaste = usePasteHandler(handleFileDrop);

  return (
    <>
      <div className="flex w-full flex-col gap-4" data-id="feature-cards-container">
        {/* <ChatPersonality /> */}
        <FeatureCards cards={features} onSelectFn={(prompt: string) => handleSubmit(prompt)} />
      </div>
      {suggestions.length > 0 && (
        <div className="flex w-full flex-col gap-4 pl-4" data-id="prompt-suggestions-container">
          <PromptSuggestions suggestions={suggestions} onSelect={setPrompt} />
        </div>
      )}

      <div
        className="flex w-full flex-col gap-4 pt-4 pb-20 sm:pb-2 px-4"
        data-id="prompt-form-container"
      >
        {workflow?.name && workflow.name !== "Chat Agent" && (
          <div className="w-full -mb-2">
            <div
              className="w-full px-4 py-1 text-small font-semibold text-white h-auto min-h-0 rounded shadow-none select-none cursor-default transition-colors flex items-center gap-2"
              data-id="workflow-label"
              style={{
                minHeight: "34px",
                backgroundColor:
                  theme === "dark" ? brand.colors.dark.tertiary : brand.colors.light.tertiary,
              }}
            >
              <Icon className="text-lg text-white" icon="gravity-ui:face-robot" />
              <span className="ml-1">
                {t(`workflow.${workflow.name}`, { defaultValue: workflow.name })}
              </span>
            </div>
          </div>
        )}
        <form
          className={cn(
            "flex w-full flex-col items-start rounded-medium bg-default-100 border-2 hover:border-brand-primary focus-within:border-brand-primary",
            isDragging && "border-primary border-dashed",
            theme === "light" && "bg-brand-secondary",
          )}
          data-id="prompt-form"
          style={{
            borderColor: isDragging
              ? undefined
              : theme === "dark"
                ? brand.colors.dark.tertiary
                : brand.colors.light.tertiary,
          }}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onSubmit={handleSubmitEvent}
        >
          <PromptInputInvestAI
            disabled={false}
            hasFilesUploading={uploadedFiles.some(
              (f) => f.status === t("Uploading") || f.status === t("Processing"),
            )}
            isReadOnly={isPromptLocked}
            isUploading={isUploading}
            messagesCount={messagesCount}
            streaming={streaming}
            value={prompt}
            onPaste={handlePaste}
            onStopStreaming={() => stopStreaming(adminClient as any)}
            onSubmit={handleSubmitEvent}
            onValueChange={isPromptLocked ? () => {} : setPrompt}
          />
          <div className="flex w-full flex-col gap-2 px-4 pb-4" data-id="prompt-actions-container">
            <div className="flex w-full items-center justify-between gap-2">
              <div
                className="flex w-full flex-wrap gap-1 md:gap-3"
                data-id="action-buttons-container"
              >
                <div className="flex items-center gap-2 px-2 hidden">
                  <span className="text-tiny text-default-500">LLM</span>
                  <Switch
                    color="primary"
                    data-id="ocr-mode-switch"
                    isSelected={isOCRMode}
                    size="sm"
                    onValueChange={setIsOCRMode}
                  />
                  <span className="text-tiny text-default-500">OCR</span>
                </div>

                {showFileUpload && (
                  <div className="flex">
                    <Button
                      isIconOnly
                      aria-label="Upload File"
                      className="flex-shrink-0 min-w-fit max-w-[35px] md:flex-1 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent"
                      data-id="upload-file-button"
                      size="sm"
                      startContent={
                        <Icon
                          className="text-brand-primary dark:!text-white"
                          icon="solar:paperclip-linear"
                          width={18}
                        />
                      }
                      variant="flat"
                      onPress={() => {
                        fileInputRef.current?.click();
                        // Remove focus to prevent Enter key from triggering again
                        setTimeout(() => {
                          blurActiveElement();
                        }, 0);
                      }}
                    >
                      <span className="hidden sm:inline" />
                    </Button>
                    <TooltipPopover>
                      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2">
                        <span className="text-brand-primary dark:!text-white text-sm">
                          {t("Upload files to include in your conversation")}
                        </span>
                      </div>
                    </TooltipPopover>
                    <input
                      ref={fileInputRef}
                      multiple
                      accept={ALLOWED_FILE_TYPES.join(",")}
                      data-id="upload-file-input"
                      style={{ display: "none" }}
                      type="file"
                      onChange={handleFileChange}
                    />
                  </div>
                )}

                {showVoice && browserSupportsSpeechRecognition && (
                  <div className="flex">
                    <Button
                      isIconOnly
                      aria-label="Voice"
                      className="flex-shrink-0 min-w-fit max-w-[35px] md:flex-1 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent disabled:bg-gray-200 disabled:cursor-not-allowed"
                      color={listening ? "danger" : "default"}
                      data-id="voice-button"
                      disabled={isPromptLocked}
                      size="sm"
                      startContent={
                        <Icon
                          className={cn(
                            isPromptLocked
                              ? "text-gray-400"
                              : listening
                                ? "text-red-600"
                                : "text-brand-primary dark:!text-white",
                          )}
                          icon={listening ? "solar:stop-circle-linear" : "solar:microphone-linear"}
                          width={18}
                        />
                      }
                      variant="flat"
                      onPress={handleVoicePress}
                    />
                    <TooltipPopover padding="p-2" width="w-[200px]">
                      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2">
                        <span className="text-brand-primary text-sm">
                          {isPromptLocked
                            ? t("Voice input disabled for this workflow")
                            : listening
                              ? t("Stop voice recording")
                              : t("Start voice recording")}
                        </span>
                      </div>
                    </TooltipPopover>
                  </div>
                )}
                {speechError && <div className="text-danger text-sm mt-1">{speechError}</div>}
                {showPrompts && (
                  <div className="flex gap-1">
                    <Button
                      className="flex-shrink-0 min-w-fit max-w-[100px] md:flex-1 border border-brand-primary hover:border-brand-primary focus-within:border-brand-primary disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-300"
                      data-id="prompts-button"
                      disabled={isPromptLocked}
                      radius="full"
                      size="sm"
                      startContent={
                        <Icon
                          className={
                            isPromptLocked ? "text-gray-400" : "text-brand-primary dark:!text-white"
                          }
                          icon="solar:code-bold"
                          width={18}
                        />
                      }
                      variant="flat"
                      onPress={() => {
                        if (!isPromptLocked) {
                          setIsTemplateModalOpen(true);
                          // Remove focus to prevent Enter key from triggering again
                          setTimeout(() => {
                            blurActiveElement();
                          }, 0);
                        }
                      }}
                    >
                      <span className="hidden sm:inline">{t("Prompts")}</span>
                    </Button>
                    <TooltipPopover>
                      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2">
                        <span className="text-brand-primary text-sm">
                          {isPromptLocked
                            ? t("Prompts disabled for this workflow")
                            : t("Browse and select from saved prompts")}
                        </span>
                      </div>
                    </TooltipPopover>
                  </div>
                )}
                {showPrompto && (
                  <div className="flex gap-1">
                    <Button
                      className="flex-shrink-0 min-w-fit max-w-[100px] md:flex-1 border border-brand-primary hover:border-brand-primary focus-within:border-brand-primary"
                      color={isOnPromptoWorkflow ? "primary" : "default"}
                      data-id="prompto-button"
                      radius="full"
                      size="sm"
                      startContent={
                        <Image
                          height={16}
                          radius="none"
                          src={`/brands/ii/prompto-icon.svg`}
                          style={
                            theme === "dark" ? { filter: "invert(1) brightness(2)" } : undefined
                          }
                        />
                      }
                      style={
                        isOnPromptoWorkflow
                          ? {
                              backgroundColor:
                                theme === "dark"
                                  ? brand.colors.dark.tertiary
                                  : brand.colors.light.tertiary,
                              color: "white",
                              borderColor:
                                theme === "dark"
                                  ? brand.colors.dark.tertiary
                                  : brand.colors.light.tertiary,
                            }
                          : undefined
                      }
                      variant="flat"
                      onPress={() => {
                        navigate("/workflows/prompt_generator_workflow");
                      }}
                    >
                      <span className="hidden sm:inline">{t("Prompto")}</span>
                    </Button>
                    <TooltipPopover padding="p-2" width="w-[300px]">
                      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2">
                        <strong>◦ {t("Prompto")}:</strong>
                        <br />
                        <br />
                        <span className="text-brand-primary text-sm">
                          {t("Prompto description")}
                        </span>
                      </div>
                    </TooltipPopover>
                  </div>
                )}
                {showSearch && (
                  <div className="flex gap-1 items-center">
                    <div className="flex items-center gap-2 py-1 px-2">
                      <Icon
                        className="text-brand-primary dark:!text-white"
                        icon="solar:global-linear"
                        width={18}
                      />
                      <span className="text-tiny text-default-500">{t("Web Search")}</span>
                      <Switch
                        color={isSearchEnabled ? "success" : "default"}
                        isSelected={isSearchEnabled}
                        size="sm"
                        onValueChange={handleWebSearchToggle}
                      />
                    </div>
                    <TooltipPopover>
                      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2">
                        <span className="text-brand-primary text-sm">
                          <strong>◦ Real-time Web Search:</strong>
                          <br />
                          <br />
                          {t("Web Search description")}
                          <br />
                          <br />
                          {t("Web Search description 2")}
                        </span>
                      </div>
                    </TooltipPopover>
                  </div>
                )}
                {showKnowledgeHub && (
                  <Tooltip
                    showArrow
                    content={
                      isKnowledgeHubEnabled ? t("Disable knowledge hub") : t("Enable knowledge hub")
                    }
                  >
                    <Button
                      className="flex-shrink-0 min-w-fit max-w-[100px] md:flex-1 border border-brand-primary hover:border-brand-primary focus-within:border-brand-primary"
                      color={isKnowledgeHubEnabled ? "primary" : "default"}
                      data-id="knowledge-hub-button"
                      radius="full"
                      size="sm"
                      startContent={
                        <Icon
                          className="text-brand-primary dark:!text-white"
                          icon="solar:user-linear"
                          width={18}
                        />
                      }
                      variant="flat"
                      onPress={handleKnowledgeHub}
                    >
                      <span className="hidden sm:inline">{t("Knowledge Hub")}</span>
                    </Button>
                  </Tooltip>
                )}

                {showSettings && (
                  <Tooltip
                    showArrow
                    content={
                      isConfigurationDisabled
                        ? isSpecialized
                          ? t("disabled-model-workflow")
                          : t("disabled-model-selector")
                        : t("Open chat configuration settings")
                    }
                  >
                    <Button
                      className="flex-shrink-0 min-w-fit max-w-[100px] md:flex-1  border border-brand-primary hover:border-brand-primary focus-within:border-brand-primary disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-300"
                      data-id="chat-history-button"
                      disabled={isConfigurationDisabled}
                      radius="full"
                      size="sm"
                      startContent={
                        <Icon
                          className={
                            isConfigurationDisabled
                              ? "text-gray-400"
                              : "text-brand-primary dark:!text-white"
                          }
                          icon="solar:settings-linear"
                          width={18}
                        />
                      }
                      variant="flat"
                      onPress={() => {
                        if (!isConfigurationDisabled) {
                          setIsSettingsModalOpen(true);
                          // Remove focus to prevent Enter key from triggering again
                          setTimeout(() => {
                            blurActiveElement();
                          }, 0);
                        }
                      }}
                    >
                      <span className="hidden sm:inline">{t("Configuration")}</span>
                    </Button>
                  </Tooltip>
                )}
                {showChatModelSelector && brand.name !== "Invest Ai" && <ChatModelSelector />}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {isPromptLocked && (
                  <Tooltip
                    showArrow
                    content={t("Input is pre-filled and locked for this workflow")}
                  >
                    <Icon
                      className="text-default-400"
                      data-id="prompt-locked-icon"
                      icon="solar:lock-linear"
                      width={14}
                    />
                  </Tooltip>
                )}
                <p
                  className="py-1 text-tiny text-default-400 tabular-nums min-w-[5ch] text-right"
                  data-id="character-count"
                >
                  {prompt.length}
                </p>
              </div>
            </div>

            {isUploading && (
              <div className="w-full" data-id="upload-progress-container">
                <Progress
                  showValueLabel
                  aria-label="Upload Progress"
                  className="w-full"
                  color="primary"
                  data-id="upload-progress-bar"
                  value={uploadProgress}
                />
              </div>
            )}

            {errorState.hasError && errorState.error && (
              <div className="space-y-2">
                <FileUploadError
                  data-testid="upload-error-message"
                  error={errorState.error}
                  showRemove={true}
                  showRetry={!!retryUpload}
                  onRemove={clearError}
                  onRetry={retryUpload || undefined}
                />

                {failedFiles.length > 1 && (
                  <div className="flex justify-center">
                    <Button
                      color="danger"
                      data-testid="retry-all-failed-button"
                      isDisabled={isUploading}
                      size="sm"
                      variant="flat"
                      onPress={retryFailedUploads}
                    >
                      {t("fileUpload.retryAllFailed", {
                        defaultValue: `Retry All Failed (${failedFiles.length})`,
                        count: failedFiles.length,
                      })}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2" data-id="uploaded-files-container">
                {uploadedFiles.map((file) => {
                  const isUploading =
                    file.status === t("Uploading") || file.status === t("Processing");

                  return (
                    <Tooltip
                      key={file.id}
                      showArrow
                      content={isUploading ? `${file.status}...` : `Remove ${file.name}`}
                    >
                      <Button
                        aria-label="Remove File"
                        data-id={`file-${file.id}`}
                        endContent={
                          <Icon
                            className={
                              isUploading
                                ? "cursor-not-allowed text-gray-400"
                                : "cursor-pointer hover:text-danger"
                            }
                            data-id={`remove-file-${file.id}`}
                            icon="solar:close-circle-linear"
                            width={16}
                            onClick={isUploading ? undefined : () => removeFile(file.name)}
                          />
                        }
                        size="sm"
                        startContent={<Icon icon={convertFileTypeToIcon(file.type)} width={16} />}
                        variant="flat"
                      >
                        <span data-id={`upload-file-name-${file.name}`}>{file.name}</span>
                        {file.status && (
                          <span className="ml-2 text-xs text-gray-500">({file.status})</span>
                        )}
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </div>
      <PromptSearchModal
        data-id="prompt-generator-modal"
        isOpen={isTemplateModalOpen}
        onOpenChange={setIsTemplateModalOpen}
        onSelectTemplate={handleTemplateSelect}
      />
      <ChatSettingsModal
        data-id="chat-settings-modal"
        isOpen={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
      />
      <ConfirmationDialog isOpen={isConfirmDialogOpen} {...confirmationProps} />
    </>
  );
};
